import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle, AlertCircle, Info, Shield, ChevronDown, ChevronRight, Download, Loader2, Search, Lock, Plus, Pencil, Trash2, Eye, ToggleLeft, ToggleRight, Settings, Calendar, RefreshCw, Users, MapPin, FileText, SlidersHorizontal } from 'lucide-react';
import BeatBriefingsPanel from '@/components/BeatBriefingsPanel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { GeoFilters } from '@/components/GeoFilters';
import { DataFreshnessBanner } from '@/components/DataFreshnessBanner';
import { formatDisplayDate } from '@/utils/dateFormat';

const severityConfig = {
  critical: { icon: AlertTriangle, color: 'text-red-600 dark:text-red-400', border: 'border-l-red-500', bg: 'bg-red-50 dark:bg-red-950/20' },
  warning: { icon: AlertCircle, color: 'text-amber-600 dark:text-amber-400', border: 'border-l-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/20' },
  info: { icon: Info, color: 'text-blue-600 dark:text-blue-400', border: 'border-l-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/20' },
};
const metricLabels = { order_value_drop: 'Order Value Drop', sku_order_drop: 'SKU Order Drop', no_orders: 'No Orders', low_sku_breadth: 'Low SKU Breadth' };
const metricDescriptions = { order_value_drop: 'Outlet order value drops beyond threshold vs previous months', sku_order_drop: 'Distinct SKU count drops beyond threshold', no_orders: 'Outlet with previous orders places zero orders', low_sku_breadth: 'Outlet orders fewer SKUs than most in same beat' };

// Parse SKU details from message for inline display
function parseAlertMessage(message) {
  const PREFIXES = ["Biggest drops: ", "Stopped: ", "Dropped SKUs: ", "Top SKUs previously: "];
  let firstIdx = message?.length || 0;
  for (const prefix of PREFIXES) {
    for (const sep of [". ", "."]) {
      const idx = (message || '').indexOf(sep + prefix);
      if (idx >= 0 && idx < firstIdx) firstIdx = idx;
    }
  }
  if (firstIdx >= (message?.length || 0)) return { main: message || '', sections: [] };
  const main = (message || '').substring(0, firstIdx + 1).trim();
  let remaining = (message || '').substring(firstIdx + 2).trim();
  const sections = [];
  while (remaining) {
    let found = null;
    for (const p of PREFIXES) { if (remaining.startsWith(p)) { found = p; break; } }
    if (!found) break;
    const contentStart = found.length;
    let nextSec = remaining.length;
    for (const np of PREFIXES) {
      for (const sep of [". ", "."]) {
        const ni = remaining.indexOf(sep + np, contentStart);
        if (ni >= 0 && ni < nextSec) nextSec = ni;
      }
    }
    const content = remaining.substring(contentStart, nextSec).trim().replace(/\.$/, '');
    remaining = nextSec < remaining.length ? remaining.substring(nextSec + 2).trim() : '';
    const items = content.split('),').map(s => s.trim()).filter(Boolean).map(s => s.endsWith(')') ? s : s + ')');
    if (items.length) sections.push({ name: found.replace(/:\s*$/, ''), items });
  }
  return { main, sections };
}

// Alert card component with structured sections
function AlertCard({ alert, onAck, onResolve }) {
  const cfg = severityConfig[alert.severity] || severityConfig.info;
  const { main, sections } = parseAlertMessage(alert.message);
  const peak = alert.peak_value || 0;
  const l3m = alert.l3m_avg || 0;
  const vc = alert.visit_count || 0;
  const hasVisitData = peak > 0 || l3m > 0 || vc > 0;

  return (
    <div className={`px-4 py-3 border-l-[3px] ${cfg.border} ${alert.resolved ? 'opacity-40' : ''} border-b border-border/20`}>
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold">{alert.outlet_name}</span>
        <span className={`text-[10px] font-heading uppercase px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>{alert.rule_name}</span>
        {alert.acknowledged && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">Ack'd</span>}
        {alert.resolved && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">Resolved</span>}
      </div>

      {/* Alert Summary */}
      <p className="text-xs text-foreground mt-1.5 font-medium">{main}</p>

      {/* Visit Context */}
      {hasVisitData && (
        <div className="mt-2 bg-muted/30 rounded px-3 py-2 space-y-1">
          <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Visit Context</p>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px]">
            {peak > 0 && <span>Peak: <strong className="text-foreground">INR {peak.toLocaleString('en-IN')}</strong></span>}
            {l3m > 0 && <span>L3M Avg: <strong className="text-foreground">INR {l3m.toLocaleString('en-IN')}</strong></span>}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {vc === 0 && <span className="text-amber-600 dark:text-amber-400 font-medium">No visit this month yet</span>}
            {vc === 1 && (
              <span>Visit 1: <strong className="text-foreground">INR {(alert.visit1_amount || 0).toLocaleString('en-IN')}</strong> ({alert.visit1_skus || 0} SKUs) on {formatDisplayDate(alert.visit1_date)}
                {peak > 0 && alert.visit1_amount < peak && <span className="text-amber-600 dark:text-amber-400 ml-2">Gap: INR {(peak - (alert.visit1_amount || 0)).toLocaleString('en-IN')}</span>}
              </span>
            )}
            {vc >= 2 && <span>{vc} visits this month</span>}
          </div>
        </div>
      )}

      {/* SKU Details */}
      {sections.length > 0 && (
        <div className="mt-2 space-y-1">
          {sections.map((sec, si) => (
            <div key={si}>
              <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">{sec.name}</p>
              <ul className="ml-3 space-y-0.5">
                {sec.items.map((item, ii) => (
                  <li key={ii} className="text-[11px] text-muted-foreground before:content-['•'] before:mr-1.5 before:text-muted-foreground/50">{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Action + Acknowledge */}
      <div className="mt-2 flex items-center justify-between">
        <p className="text-[11px] text-primary/80 italic">{alert.action}</p>
        <div className="flex items-center gap-3 flex-shrink-0">
          {!alert.acknowledged && !alert.resolved && <button onClick={(e) => { e.stopPropagation(); onAck?.(alert); }} className="text-[11px] text-primary font-medium hover:underline">Acknowledge</button>}
          {alert.acknowledged && !alert.resolved && <button onClick={(e) => { e.stopPropagation(); onResolve?.(alert); }} className="text-[11px] text-green-600 font-medium hover:underline">Resolve</button>}
        </div>
      </div>
    </div>
  );
}

// Rule-engine alerts: batches, filters, ack/resolve, rule editor and the FE
// Alerts / Raw Alerts downloads. Kept behind the admin-only "Advanced" tab so
// the day-to-day Beat Briefings workflow stays uncluttered.
function AdvancedAlertsPanel() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [alertData, setAlertData] = useState(null);
  const [batches, setBatches] = useState([]);
  const [rules, setRules] = useState([]);
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [sevFilter, setSevFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [groupBy, setGroupBy] = useState('fe'); // 'fe' | 'beat'
  const [genOpen, setGenOpen] = useState(false);
  const [genDate, setGenDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [periodPreview, setPeriodPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name:'', description:'', metric:'order_value_drop', threshold:30, period_months:3, severity:'warning', min_previous_value:1000 });
  const [creating, setCreating] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [clearStep, setClearStep] = useState(0);
  const [clearOtp, setClearOtp] = useState('');
  const [clearLoading, setClearLoading] = useState(false);
  const [clearError, setClearError] = useState('');
  const [downloading, setDownloading] = useState('');
  const [targetData, setTargetData] = useState(null);
  // Ack/Resolve confirmation
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'ack'|'resolve', alert }
  const [confirmDontAsk, setConfirmDontAsk] = useState(false);

  useEffect(() => { api.get('/alerts/fe/targets').then(r => setTargetData(r.data)).catch(() => {}); }, [api]);
  const loadRules = () => api.get('/alerts/rules').then(r => setRules(r.data)).catch(() => {});
  const loadBatches = () => api.get('/alerts/batches').then(r => setBatches(r.data)).catch(() => {});
  const loadAlerts = (batchId, forDate) => {
    setLoading(true);
    const params = {};
    if (batchId) params.batch_id = batchId;
    if (forDate) params.generated_for_date = forDate;
    if (zone) params.zone = zone;
    if (subzone) params.subzone = subzone;
    if (state) params.state = state;
    if (city) params.city = city;
    if (sevFilter) params.severity = sevFilter;
    const isNewBatch = batchId || forDate;
    api.get('/alerts', { params }).then(r => { setAlertData(r.data); if (isNewBatch) setExpandedGroups(new Set()); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadRules(); loadBatches(); loadAlerts(); }, [api]);
  const filterInitRef = React.useRef(false);
  useEffect(() => { if (!filterInitRef.current) { filterInitRef.current = true; return; } if (alertData?.batch) loadAlerts(alertData.batch.id); }, [zone, subzone, state, city, sevFilter]);
  useEffect(() => { if (genDate && genOpen) api.get(`/alerts/preview-period?date=${genDate}`).then(r => setPeriodPreview(r.data)).catch(() => {}); }, [genDate, genOpen, api]);

  const handleGenerate = async () => {
    setGenerating(true);
    try { const r = await api.post('/alerts/generate', { date: genDate }); toast.success(`${r.data.summary.total.toLocaleString()} alerts generated`); setGenOpen(false); loadBatches(); loadAlerts(null, genDate); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setGenerating(false); }
  };
  const handleAck = async (id) => { await api.patch(`/alerts/acknowledge/${id}`); };
  const handleResolve = async (id) => { await api.patch(`/alerts/resolve/${id}`); };

  // Optimistic local state update
  const updateAlertLocally = (id, updates) => {
    setAlertData(prev => {
      if (!prev) return prev;
      return { ...prev, alerts: prev.alerts.map(a => a.id === id ? { ...a, ...updates } : a) };
    });
  };

  const executeAction = async (type, alert) => {
    try {
      if (type === 'ack') {
        await handleAck(alert.id);
        updateAlertLocally(alert.id, { acknowledged: true, acknowledged_by: 'You' });
        toast.success(`Acknowledged: ${alert.outlet_name}`);
      } else {
        await handleResolve(alert.id);
        updateAlertLocally(alert.id, { resolved: true, resolved_by: 'You' });
        toast.success(`Resolved: ${alert.outlet_name}`);
      }
    } catch { toast.error('Failed to update'); }
  };

  const requestAction = (type, alert) => {
    const skipKey = type === 'ack' ? 'keya_skip_ack_confirm' : 'keya_skip_resolve_confirm';
    if (sessionStorage.getItem(skipKey) === 'true') {
      executeAction(type, alert);
    } else {
      setConfirmAction({ type, alert });
      setConfirmDontAsk(false);
    }
  };

  const confirmAndExecute = () => {
    if (!confirmAction) return;
    if (confirmDontAsk) {
      const skipKey = confirmAction.type === 'ack' ? 'keya_skip_ack_confirm' : 'keya_skip_resolve_confirm';
      sessionStorage.setItem(skipKey, 'true');
    }
    executeAction(confirmAction.type, confirmAction.alert);
    setConfirmAction(null);
  };
  const handleEditSave = async () => { setSaving(true); try { await api.patch(`/alerts/rules/${editRule.id}`, editForm); toast.success('Updated'); setEditRule(null); loadRules(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); } };
  const handleCreate = async () => { setCreating(true); try { await api.post('/alerts/rules', createForm); toast.success('Created'); setCreateOpen(false); setCreateForm({name:'',description:'',metric:'order_value_drop',threshold:30,period_months:3,severity:'warning',min_previous_value:1000}); loadRules(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setCreating(false); } };
  const handleDeleteRule = async (rule) => { try { await api.delete(`/alerts/rules/${rule.id}`); toast.success('Deleted'); loadRules(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const handleToggleRule = async (rule) => { try { await api.patch(`/alerts/rules/${rule.id}`, { enabled: !rule.enabled }); loadRules(); } catch {} };
  const handlePreview = async (ruleId) => { setPreviewing(true); setPreviewCount(null); try { const r = ruleId ? await api.post(`/alerts/rules/${ruleId}/preview`) : await api.post('/alerts/rules/preview-custom', createForm); setPreviewCount(r.data.outlets_affected); } catch {} finally { setPreviewing(false); } };

  const handleDownload = async (fmt) => {
    if (!alertData?.batch) return;
    setDownloading(fmt);
    try {
      const params = new URLSearchParams({ date: alertData.batch.generated_for_date, fmt, group_by: groupBy });
      if (zone) params.set('zone', zone);
      const res = await api.get(`/alerts/fe/download?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `keya_pulse_alerts_${groupBy}_${alertData.batch.generated_for_date}.zip`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch { toast.error('Download failed'); } finally { setDownloading(''); }
  };

  const handleBriefingDownload = async () => {
    if (!alertData?.batch) return;
    setDownloading('briefing');
    try {
      const params = new URLSearchParams({ date: alertData.batch.generated_for_date, fmt: 'both' });
      if (zone) params.set('zone', zone);
      const res = await api.get(`/alerts/fe/briefing?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `keya_pulse_briefings_${alertData.batch.generated_for_date}.zip`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Briefings downloaded');
    } catch { toast.error('Briefing download failed'); } finally { setDownloading(''); }
  };

  const toggleGroup = (key) => { const s = new Set(expandedGroups); s.has(key) ? s.delete(key) : s.add(key); setExpandedGroups(s); };

  const batch = alertData?.batch;
  const allAlerts = alertData?.alerts || [];

  // Apply search filter on top of server-side zone/severity filters
  const filtered = useMemo(() => allAlerts.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return a.outlet_name?.toLowerCase().includes(q) || a.beat?.toLowerCase().includes(q) || a.fe_name?.toLowerCase().includes(q)
      || a.fe_username?.toLowerCase().includes(q) || a.message?.toLowerCase().includes(q);
  }), [allAlerts, search]);

  // Compute summary: use backend summary for zone/severity filtered totals, overlay with local search filter
  const serverSummary = alertData?.summary || {};
  const filteredSummary = useMemo(() => {
    if (search) {
      // Search is local — compute from filtered list
      return {
        total: filtered.length,
        critical: filtered.filter(a => a.severity === 'critical').length,
        warning: filtered.filter(a => a.severity === 'warning').length,
        info: filtered.filter(a => a.severity === 'info').length,
      };
    }
    // No search — use server totals (accurate, not capped by LIMIT)
    return {
      total: serverSummary.total || 0,
      critical: serverSummary.critical || 0,
      warning: serverSummary.warning || 0,
      info: serverSummary.info || 0,
    };
  }, [filtered, search, serverSummary]);

  // Group alerts
  const groups = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const key = groupBy === 'fe' ? (a.fe_username || a.fe_name || 'Unassigned') : (a.beat || 'Unknown');
      if (!map[key]) {
        map[key] = groupBy === 'fe'
          ? { label: a.fe_name || 'Unassigned', sub: a.fe_username || '', detail: `Reports to: ${a.reporting_to || '-'}`, alerts: [] }
          : { label: a.beat || 'Unknown', sub: '', detail: `FE: ${a.fe_name || '-'} | Zone: ${a.zone || '-'}`, alerts: [] };
      }
      map[key].alerts.push(a);
    });
    return Object.entries(map).sort((a, b) => b[1].alerts.filter(x => x.severity === 'critical').length - a[1].alerts.filter(x => x.severity === 'critical').length);
  }, [filtered, groupBy]);

  const fesNeedingAttention = groups.filter(([, d]) => d.alerts.some(a => a.severity === 'critical')).length;
  const topGroups = groups.slice(0, 5);

  return (
    <div className="space-y-5" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-heading font-semibold tracking-tight">Rule-Based Alerts</h2>
          {batch && <p className="text-xs text-muted-foreground mt-1">{formatDisplayDate(batch.generated_for_date)} &middot; {batch.current_period} vs {batch.comparison_period} &middot; by {batch.generated_by}</p>}
        </div>
        <div className="flex items-center gap-2">
          {batches.length > 0 && (
            <Select value={batch?.id || ''} onValueChange={v => loadAlerts(v)}>
              <SelectTrigger className="w-52 h-8 text-xs"><Calendar size={12} className="mr-1.5" /><SelectValue placeholder="Select batch" /></SelectTrigger>
              <SelectContent>{batches.map(b => <SelectItem key={b.id} value={b.id}>{formatDisplayDate(b.generated_for_date)} ({b.total_alerts?.toLocaleString()})</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={() => setGenOpen(true)} className="h-8 text-xs" data-testid="new-batch-btn"><RefreshCw size={12} className="mr-1.5" /> New Batch</Button>
          <Button variant="ghost" size="icon" onClick={() => { setRulesOpen(true); loadRules(); }} className="h-8 w-8" data-testid="rules-btn"><Settings size={16} /></Button>
        </div>
      </div>

      {/* Tweak 1: Filters ABOVE summary */}
      {batch && (
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search FE, user ID, beat, outlet, SKU..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 w-64 h-8 text-xs" data-testid="alert-search" />
          </div>
          <GeoFilters api={api} size="sm" zone={zone} setZone={setZone}
            subzone={subzone} setSubzone={setSubzone} state={state} setState={setState}
            city={city} setCity={setCity} />
          <Select value={sevFilter || 'all'} onValueChange={v => setSevFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="All Severity" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="critical">Critical</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="info">Info</SelectItem></SelectContent>
          </Select>
          {/* Tweak 4: Group by toggle */}
          <div className="flex gap-0.5 bg-muted/50 rounded-md p-0.5">
            <button onClick={() => { setGroupBy('fe'); setExpandedGroups(new Set()); }} className={`px-2.5 py-1 text-[11px] font-heading font-medium rounded flex items-center gap-1 ${groupBy === 'fe' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="group-by-fe">
              <Users size={11} /> FE
            </button>
            <button onClick={() => { setGroupBy('beat'); setExpandedGroups(new Set()); }} className={`px-2.5 py-1 text-[11px] font-heading font-medium rounded flex items-center gap-1 ${groupBy === 'beat' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="group-by-beat">
              <MapPin size={11} /> Beat
            </button>
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={handleBriefingDownload} disabled={!!downloading || !batch} className="h-8 text-xs border-primary/30 text-primary hover:bg-primary/5" data-testid="download-briefing-btn">
              {downloading === 'briefing' ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Download size={12} className="mr-1.5" />} FE Alerts
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDownload('both')} disabled={!!downloading || !batch} className="h-8 text-xs" data-testid="download-all-btn">
              {downloading && downloading !== 'briefing' ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Download size={12} className="mr-1.5" />} Raw Alerts
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setClearStep(1); setClearOtp(''); setClearError(''); }}
              className="h-8 text-xs border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30" data-testid="clear-alerts-btn">
              <Trash2 size={12} className="mr-1.5" /> Clear
            </Button>
          </div>
        </div>
      )}

      <DataFreshnessBanner api={api} />

      {/* Tweak 2: Summary from FILTERED data */}
      {batch && !loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-card border border-border rounded-md p-3">
              <p className="kpi-label text-muted-foreground">{groupBy === 'fe' ? 'FEs' : 'Beats'} Need Attention</p>
              <p className="text-xl font-heading font-bold mt-0.5 text-red-600 dark:text-red-400">{fesNeedingAttention}</p>
            </div>
            <div className="bg-card border border-border rounded-md p-3">
              <p className="kpi-label text-muted-foreground">Total Alerts</p>
              <p className="text-xl font-heading font-bold mt-0.5">{filteredSummary.total.toLocaleString()}</p>
            </div>
            <div className="bg-card border border-red-200 dark:border-red-900 rounded-md p-3">
              <p className="kpi-label text-red-600 dark:text-red-400">Critical</p>
              <p className="text-xl font-heading font-bold mt-0.5 text-red-600 dark:text-red-400">{filteredSummary.critical.toLocaleString()}</p>
            </div>
            <div className="bg-card border border-amber-200 dark:border-amber-900 rounded-md p-3">
              <p className="kpi-label text-amber-600 dark:text-amber-400">Warning</p>
              <p className="text-xl font-heading font-bold mt-0.5 text-amber-600 dark:text-amber-400">{filteredSummary.warning.toLocaleString()}</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-md p-3">
            <p className="kpi-label text-muted-foreground mb-2">Most Impacted {groupBy === 'fe' ? 'FEs' : 'Beats'}</p>
            <div className="space-y-1.5">
              {topGroups.map(([key, d]) => {
                const crit = d.alerts.filter(a => a.severity === 'critical').length;
                return (
                  <div key={key} className="flex items-center justify-between text-xs cursor-pointer hover:bg-muted/30 rounded px-1.5 py-1 -mx-1.5"
                    onClick={() => { setExpandedGroups(new Set([key])); document.getElementById(`grp-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
                    <span className="font-medium truncate max-w-[180px]">{d.label}</span>
                    <div className="flex gap-1.5">
                      {crit > 0 && <span className="text-red-600 dark:text-red-400 font-medium">{crit}</span>}
                      <span className="text-muted-foreground">{d.alerts.length}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Target Achievement */}
      {targetData?.targets?.length > 0 && targetData.targets.some(t => t.mtd > 0) && (
        <div className="bg-card border border-border rounded-md p-4" data-testid="target-achievement-panel">
          <div className="mb-3">
            <h3 className="font-heading font-semibold text-sm">FE Target Achievement — {targetData.month}</h3>
            <p className="text-[10px] text-muted-foreground mt-0.5">Monthly sales target vs MTD (month-to-date) fulfilled from Order Fulfilment data</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-auto">
            {targetData.targets.filter(t => t.mtd > 0 || t.target > 0).slice(0, 15).map(t => {
              const pct = Math.min(t.achievement_pct, 100);
              const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
              return (
                <div key={t.user_id} className="flex items-center gap-2 text-xs">
                  <span className="w-28 truncate font-medium" title={t.name}>{t.name}</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-10 text-right text-muted-foreground">{t.achievement_pct}%</span>
                </div>
              );
            })}
          </div>
          {targetData.targets.length > 15 && <p className="text-[10px] text-muted-foreground mt-2">Showing top 15 of {targetData.targets.length} FEs</p>}
        </div>
      )}

      {/* Note */}
      {batch?.data_note && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md px-4 py-2 text-xs text-amber-700 dark:text-amber-400"><strong>Note:</strong> {batch.data_note}</div>
      )}

      {/* Grouped alerts (collapsible) with full inline detail (Tweak 5) */}
      {loading ? (
        <div className="text-center py-16 text-muted-foreground text-sm flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : !batch ? (
        <div className="bg-card border border-border rounded-md p-16 text-center">
          <AlertTriangle size={40} strokeWidth={1} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading font-semibold text-lg mb-2">No Alerts Generated Yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Generate your first batch to start monitoring.</p>
          <Button onClick={() => setGenOpen(true)}><RefreshCw size={14} className="mr-1.5" /> Generate Alerts</Button>
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-card border border-border rounded-md p-16 text-center">
          <Shield size={40} strokeWidth={1} className="mx-auto text-green-600 dark:text-green-400 mb-4" />
          <h3 className="font-heading font-semibold text-lg mb-1">All Clear</h3>
          <p className="text-sm text-muted-foreground">No alerts match your filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map(([key, data]) => {
            const expanded = expandedGroups.has(key);
            const critCnt = data.alerts.filter(a => a.severity === 'critical').length;
            const warnCnt = data.alerts.filter(a => a.severity === 'warning').length;

            // Sub-group: if groupBy=fe, sub-group by beat; if groupBy=beat, just list
            const subGroups = {};
            if (groupBy === 'fe') {
              data.alerts.forEach(a => { const b = a.beat || 'Unknown'; if (!subGroups[b]) subGroups[b] = []; subGroups[b].push(a); });
            } else {
              subGroups['_all'] = data.alerts;
            }

            return (
              <div key={key} id={`grp-${key}`} className="bg-card border border-border rounded-md overflow-hidden">
                <button onClick={() => toggleGroup(key)} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors text-left">
                  {expanded ? <ChevronDown size={14} className="text-muted-foreground flex-shrink-0" /> : <ChevronRight size={14} className="text-muted-foreground flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <span className="font-heading font-semibold text-sm">{data.label}</span>
                    {data.sub && <span className="text-xs text-muted-foreground ml-2">({data.sub})</span>}
                    <span className="text-[11px] text-muted-foreground ml-2 hidden sm:inline">{data.detail}</span>
                  </div>
                  <div className="flex gap-2 text-[11px] flex-shrink-0">
                    {critCnt > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 font-semibold">{critCnt} critical</span>}
                    {warnCnt > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-semibold">{warnCnt} warning</span>}
                    <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{data.alerts.length}</span>
                  </div>
                </button>
                {expanded && (
                  <div className="border-t border-border/50">
                    {Object.entries(subGroups).map(([sgKey, sgAlerts]) => (
                      <div key={sgKey}>
                        {sgKey !== '_all' && (
                          <div className="px-4 py-1.5 bg-muted/20 border-b border-border/30">
                            <span className="text-[11px] font-heading font-medium text-muted-foreground">Beat: {sgKey}</span>
                          </div>
                        )}
                        {sgAlerts.map((a) => <AlertCard key={a.id} alert={a} onAck={(al) => requestAction('ack', al)} onResolve={(al) => requestAction('resolve', al)} />)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ========== GENERATE DIALOG ========== */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="sm:max-w-md" onInteractOutside={e => e.preventDefault()} data-testid="gen-dialog">
          <DialogHeader><DialogTitle className="font-heading">Generate Alert Batch</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Date</Label>
              <Input type="date" value={genDate} onChange={e => setGenDate(e.target.value)} data-testid="gen-date-input" /></div>
            {periodPreview && (
              <div className="bg-muted/50 rounded-md px-3 py-2 text-xs text-muted-foreground">
                Comparing <strong className="text-foreground">{periodPreview.current_period}</strong> vs <strong className="text-foreground">{periodPreview.comparison_period}</strong>, data up to <strong className="text-foreground">{periodPreview.date_fmt}</strong>.
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleGenerate} disabled={generating || !genDate} data-testid="generate-btn">{generating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />} Generate</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== RULES PANEL ========== */}
      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-auto" onInteractOutside={e => e.preventDefault()} data-testid="rules-panel">
          <DialogHeader><DialogTitle className="font-heading flex items-center justify-between pr-8"><span>Alert Rules ({rules.length})</span>
            <Button size="sm" onClick={() => { setCreateOpen(true); setPreviewCount(null); }} data-testid="add-rule-btn"><Plus size={12} className="mr-1" /> Add</Button></DialogTitle></DialogHeader>
          <div className="space-y-3">
            {rules.map((rule, i) => (
              <div key={rule.id} className={`border rounded-md p-3 ${rule.enabled ? 'border-border' : 'border-border/50 opacity-60'}`} data-testid={`rule-card-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      {rule.type === 'system' && <Lock size={10} className="text-muted-foreground" />}
                      <span className="font-heading font-semibold text-xs">{rule.name}</span>
                      <span className={`text-[9px] font-heading uppercase px-1 py-0.5 rounded ${rule.type === 'system' ? 'bg-primary/10 text-primary' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'}`}>{rule.type}</span>
                      <span className={`text-[9px] font-heading uppercase px-1 py-0.5 rounded ${severityConfig[rule.severity]?.bg} ${severityConfig[rule.severity]?.color}`}>{rule.severity}</span>
                    </div>
                    <div className="flex gap-3 text-[11px] text-muted-foreground flex-wrap">
                      <span>{metricLabels[rule.metric]}</span><span>Threshold: {rule.threshold}%</span><span>Period: {rule.period_months}mo</span>
                      {rule.min_previous_value > 0 && <span>Min: INR {rule.min_previous_value?.toLocaleString('en-IN')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button onClick={() => handleToggleRule(rule)} className="p-1 rounded hover:bg-muted/50 text-muted-foreground">{rule.enabled ? <ToggleRight size={14} className="text-green-600" /> : <ToggleLeft size={14} />}</button>
                    <button onClick={() => { setEditRule(rule); setEditForm({ threshold: rule.threshold, period_months: rule.period_months, severity: rule.severity, include_no_order_outlets: rule.include_no_order_outlets||false, min_previous_value: rule.min_previous_value||0, ...(rule.type !== 'system' ? { name: rule.name } : {}) }); setPreviewCount(null); }} className="p-1 rounded hover:bg-muted/50 text-muted-foreground"><Pencil size={12} /></button>
                    {rule.type !== 'system' && (
                      <AlertDialog><AlertDialogTrigger asChild><button className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-destructive"><Trash2 size={12} /></button></AlertDialogTrigger>
                        <AlertDialogContent onInteractOutside={e => e.preventDefault()}><AlertDialogHeader><AlertDialogTitle>Delete "{rule.name}"?</AlertDialogTitle></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteRule(rule)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== EDIT RULE ========== */}
      <Dialog open={!!editRule} onOpenChange={v => { if (!v) setEditRule(null); }}>
        <DialogContent className="sm:max-w-md" onInteractOutside={e => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-heading">Edit: {editRule?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {editRule?.type !== 'system' && <div className="space-y-2"><Label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Name</Label><Input value={editForm.name||''} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Threshold %</Label><Input type="number" value={editForm.threshold??''} onChange={e => setEditForm({...editForm, threshold: parseFloat(e.target.value)||0})} /></div>
              <div className="space-y-1"><Label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Period (mo)</Label><Input type="number" value={editForm.period_months??''} onChange={e => setEditForm({...editForm, period_months: parseInt(e.target.value)||1})} /></div>
              <div className="space-y-1"><Label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Severity</Label>
                <Select value={editForm.severity||'warning'} onValueChange={v => setEditForm({...editForm, severity: v})}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="critical">Critical</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="info">Info</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-2 pt-2 border-t border-border/50">
              <Label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Min Previous Monthly Value (INR)</Label>
              <Input type="number" value={editForm.min_previous_value??0} onChange={e => setEditForm({...editForm, min_previous_value: parseFloat(e.target.value)||0})} />
            </div>
            {editRule?.metric === 'no_orders' && <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={editForm.include_no_order_outlets||false} onChange={e => setEditForm({...editForm, include_no_order_outlets: e.target.checked})} className="rounded" /><span className="text-xs text-muted-foreground">Include never-ordered outlets</span></label>}
            <div className="flex items-center gap-3"><Button variant="outline" size="sm" onClick={() => handlePreview(editRule?.id)} disabled={previewing}>{previewing ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Eye size={12} className="mr-1.5" />} Preview</Button>
              {previewCount !== null && <span className="text-xs text-muted-foreground">Affects <strong>{previewCount}</strong> outlets</span>}</div>
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setEditRule(null)}>Cancel</Button><Button size="sm" onClick={handleEditSave} disabled={saving}>{saving && <Loader2 size={14} className="mr-1.5 animate-spin" />} Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== CREATE RULE ========== */}
      <Dialog open={createOpen} onOpenChange={v => { if (!v) setCreateOpen(false); }}>
        <DialogContent className="sm:max-w-md" onInteractOutside={e => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-heading">Create Custom Rule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Name</Label><Input placeholder="e.g., Severe Drop" value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} /></div>
            <div className="space-y-2"><Label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">Metric</Label>
              <Select value={createForm.metric} onValueChange={v => setCreateForm({...createForm, metric: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(metricLabels).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              <p className="text-[11px] text-muted-foreground">{metricDescriptions[createForm.metric]}</p></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Threshold %</Label><Input type="number" value={createForm.threshold} onChange={e => setCreateForm({...createForm, threshold: parseFloat(e.target.value)||0})} /></div>
              <div className="space-y-1"><Label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Period</Label><Input type="number" value={createForm.period_months} onChange={e => setCreateForm({...createForm, period_months: parseInt(e.target.value)||1})} /></div>
              <div className="space-y-1"><Label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Severity</Label>
                <Select value={createForm.severity} onValueChange={v => setCreateForm({...createForm, severity: v})}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="critical">Critical</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="info">Info</SelectItem></SelectContent></Select></div>
            </div>
            <div className="space-y-2 pt-2 border-t border-border/50">
              <Label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Min Previous Monthly Value (INR)</Label>
              <Input type="number" value={createForm.min_previous_value||0} onChange={e => setCreateForm({...createForm, min_previous_value: parseFloat(e.target.value)||0})} />
            </div>
            {createForm.metric === 'no_orders' && <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={createForm.include_no_order_outlets||false} onChange={e => setCreateForm({...createForm, include_no_order_outlets: e.target.checked})} className="rounded" /><span className="text-xs text-muted-foreground">Include never-ordered outlets</span></label>}
            <div className="flex items-center gap-3"><Button variant="outline" size="sm" onClick={() => handlePreview(null)} disabled={previewing}>{previewing ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Eye size={12} className="mr-1.5" />} Preview</Button>
              {previewCount !== null && <span className="text-xs text-muted-foreground">Would affect <strong>{previewCount}</strong> outlets</span>}</div>
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleCreate} disabled={creating||!createForm.name}>{creating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />} Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== CLEAR ALERTS ========== */}
      <Dialog open={clearStep > 0} onOpenChange={v => { if (!v) { setClearStep(0); setClearOtp(''); setClearError(''); } }}>
        <DialogContent className="sm:max-w-md" onInteractOutside={e => e.preventDefault()} data-testid="clear-alerts-dialog">
          <DialogHeader><DialogTitle className="font-heading text-red-600 dark:text-red-400 flex items-center gap-2 pr-8"><AlertTriangle size={18} /> Clear All Alerts</DialogTitle>
            <DialogDescription>{clearStep === 1 ? 'Delete all generated alerts and batch history.' : 'Enter OTP to confirm.'}</DialogDescription></DialogHeader>
          {clearError && <p className="text-sm text-destructive font-medium">{clearError}</p>}
          {clearStep === 1 && (
            <DialogFooter><Button variant="outline" size="sm" onClick={() => setClearStep(0)}>Cancel</Button>
              <Button size="sm" onClick={async () => { setClearLoading(true); setClearError(''); try { await api.post('/alerts/clear/request-otp'); setClearStep(2); } catch (e) { setClearError(e.response?.data?.detail||'Failed'); } finally { setClearLoading(false); } }}
                disabled={clearLoading} className="bg-red-600 text-white hover:bg-red-700">{clearLoading && <Loader2 size={14} className="mr-1.5 animate-spin" />} Send OTP</Button></DialogFooter>
          )}
          {clearStep === 2 && (
            <div className="space-y-4">
              <Input type="text" placeholder="123456" value={clearOtp} onChange={e => setClearOtp(e.target.value.replace(/\D/g,'').slice(0,6))} maxLength={6} className="text-center text-xl tracking-[0.5em] font-mono font-bold" />
              <DialogFooter><Button variant="outline" size="sm" onClick={() => setClearStep(0)}>Cancel</Button>
                <Button size="sm" onClick={async () => { if (clearOtp.length!==6){setClearError('Enter 6-digit OTP');return;} setClearLoading(true);setClearError(''); try{const r=await api.post('/alerts/clear/confirm',{otp:clearOtp});toast.success(r.data.message);setClearStep(0);loadAlerts();loadBatches();}catch(e){setClearError(e.response?.data?.detail||'Failed');}finally{setClearLoading(false);} }}
                  disabled={clearLoading||clearOtp.length!==6} className="bg-red-600 text-white hover:bg-red-700">{clearLoading?<Loader2 size={14} className="mr-1.5 animate-spin"/>:<Trash2 size={14} className="mr-1.5"/>} Confirm</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ========== ACK/RESOLVE CONFIRMATION ========== */}
      <Dialog open={!!confirmAction} onOpenChange={v => { if (!v) setConfirmAction(null); }}>
        <DialogContent className="sm:max-w-sm" onInteractOutside={e => e.preventDefault()} data-testid="confirm-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {confirmAction?.type === 'ack' ? 'Acknowledge Alert' : 'Resolve Alert'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === 'ack'
                ? 'Mark this alert as acknowledged — your team is aware of the issue.'
                : 'Mark this alert as resolved — the issue has been addressed.'}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-muted/50 rounded-md p-3 space-y-1">
            <p className="text-sm font-medium">{confirmAction?.alert?.outlet_name}</p>
            <p className="text-xs text-muted-foreground">{parseAlertMessage(confirmAction?.alert?.message || '').main}</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={confirmDontAsk} onChange={e => setConfirmDontAsk(e.target.checked)} className="rounded border-border" data-testid="dont-ask-again" />
            <span className="text-xs text-muted-foreground">Don't ask again for this session</span>
          </label>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button size="sm" onClick={confirmAndExecute} data-testid="confirm-action-btn">
              {confirmAction?.type === 'ack' ? 'Acknowledge' : 'Resolve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}


export default function AlertsPage() {
  // The Advanced tab is the old rule-alert machinery and its raw downloads; it stays with the
  // Super Admin, the level today's admins became.
  const { isSuper } = useAuth();
  const [tab, setTab] = useState('briefings');

  return (
    <div className="space-y-5" data-testid="beat-briefings-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Beat Briefings</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Daily action plans for every field executive — what to do at each outlet on today's beat
          </p>
        </div>
        {isSuper && (
          <div className="flex gap-0.5 bg-muted/50 rounded-md p-0.5" data-testid="alerts-tabs">
            <button onClick={() => setTab('briefings')}
              className={`px-3 py-1.5 text-xs font-heading font-medium rounded flex items-center gap-1.5 ${tab === 'briefings' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
              data-testid="tab-briefings">
              <FileText size={12} /> Beat Briefings
            </button>
            <button onClick={() => setTab('advanced')}
              className={`px-3 py-1.5 text-xs font-heading font-medium rounded flex items-center gap-1.5 ${tab === 'advanced' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}
              data-testid="tab-advanced">
              <SlidersHorizontal size={12} /> Advanced
            </button>
          </div>
        )}
      </div>

      {isSuper && tab === 'advanced' ? <AdvancedAlertsPanel /> : <BeatBriefingsPanel />}
    </div>
  );
}
